import React, {useEffect, useMemo} from "react";
import "./style.scss";
import {CategoricalKPI, TimeDelta} from "weed-js";
import {Chart as ChartJS, registerables} from "chart.js";
import {KPIChart} from "./KPIChart";
import {getTimeWindowedKPIPillColumns, calculateTimeWindowedKPIs, windowsAreDefined} from "./utils";
import {KPIPillColumn} from "./KPIPillColumn";
import Loading from "../../../../../../../components/elements/Loading";



ChartJS.register(...registerables);


export const KPIGrid = ({
    activeWindow,
    binSizeDays = 1,
    inventoryByProductId,
    product,
    setActiveWindow,
    setUserDefinedWindow,
    transactionItems,
    userDefinedWindow,
    defaultStart,
    defaultMiddle,
    defaultEnd,
    showBaseline = true, // Whether to show the baseline column
    showComparison = true, // Whether to show the comparison column
    showBaselineVSComparison = true, // Whether to show the combined column
    hideControls = false, // Whether to hide the controls for the time ranges
    showCharts = true, // Whether to show the charts below the KPIs

    titles = {
        baseline: "Baseline",
        comparison: "Comparison",
        baselineVsComparison: null
    }
}) => {

    const [inProgress, setInProgress] = React.useState(0);
    const [total, setTotal] = React.useState(0);
    const [kpis, setKPIs] = React.useState({});

    const previousAsDates = useMemo(() => {
        return {
            start: activeWindow?.previous?.start?.toDate(),
            end: activeWindow?.previous?.end?.toDate()
        }
    }, [activeWindow?.previous?.start, activeWindow?.previous?.end]);

    const currentAsDates = useMemo(() => {
        return {
            start: activeWindow?.current?.start?.toDate(),
            end: activeWindow?.current?.end?.toDate()
        }
    }, [activeWindow?.current?.start, activeWindow?.current?.end]);

    const incrementalCallback = (current, totalItems) => {
        setInProgress(current);
        setTotal(totalItems);
    }

    const onComplete = (computedKPIs) => {
        setInProgress(0);
        setTotal(0);
        setKPIs(computedKPIs);
    }

    useEffect(() => {
        if (!windowsAreDefined(activeWindow)) {
            return;
        }
        calculateTimeWindowedKPIs({
            currentProduct: product,
            transactionItems: transactionItems,
            inventoryByProductId: inventoryByProductId,
            previousWindow: previousAsDates,
            currentWindow: currentAsDates,
            incrementalCallback: incrementalCallback,
            onComplete: onComplete
        });
    }, [product, previousAsDates, currentAsDates, transactionItems, inventoryByProductId]);

    const {previousColumn, currentColumn, previousToCurrent} = useMemo(() => {
        if (!windowsAreDefined(activeWindow)) {
            return {previousColumn: [], currentColumn: [], previousToCurrent: []};
        }
        if (!previousAsDates.start || !previousAsDates.end ||
            !currentAsDates.start || !currentAsDates.end) {
            return {previousColumn: [], currentColumn: [], previousToCurrent: []};
        }
        return getTimeWindowedKPIPillColumns({
            kpis,
            previousWindow: previousAsDates,
            currentWindow: currentAsDates,
            showBaseline: showBaseline,
            showComparison: showComparison,
            showBaselineVSComparison: showBaselineVSComparison,
        });
    }, [kpis, product, previousAsDates, currentAsDates]);

    let baselineVsComparisonTitle = "Baseline vs Comparison";
    if (activeWindow?.previous?.start && activeWindow?.current?.end) {
        baselineVsComparisonTitle += [
            " (",
            TimeDelta.formatShortDate(activeWindow.previous.start.toDate()),
            "-",
            TimeDelta.formatShortDate(activeWindow.current.end.toDate()),
            ")"
        ].join("")
    }

    const getPercentComplete = (inProgress, total) => {
        if (total === 0) {
            return "0%";
        }
        return ((inProgress / total) * 100).toFixed(0) + "%";
    }

    if (inProgress > 0) {
        return <Loading title={`Calculating KPIs ${getPercentComplete(inProgress, total)}`}/>;
    }

    return <>
        <div className={"kpi-grid-columns"}>
            {showBaseline && <KPIPillColumn
                title={titles?.baseline || "Baseline"}
                tooltip={"This column should come before the next one in time. It's ok to overlap. It's a baseline against which to compare so you can get some perspective on your product's performance. An example might be last week, and then you could compare this week so far. Below we'll show you the stats over your product over that time period, and we'll look at trends for the first half of that period to the second."}
                activeWindow={activeWindow}
                column={previousColumn}
                defaultEnd={defaultEnd}
                defaultMiddle={defaultMiddle}
                defaultStart={defaultStart}
                disposition={"previous"}
                setActiveWindow={setActiveWindow}
                setUserDefinedWindow={setUserDefinedWindow}
                userDefinedWindow={userDefinedWindow}
                hideControls={hideControls}
            />}
            {(showBaseline && (showComparison || showBaselineVSComparison)) && <div className={"kpi-grid-vertical-separation"}/>}
            {showComparison && <KPIPillColumn
                title={titles?.comparison || "Comparison"}
                tooltip={"This column is another time range that falls after the Baseline. You might choose this week so far to get a perspective on whether or not your product is doing better or worse than it was. Below we'll show the stats for your product and we'll compare how it did in the first half of the period you choose compared to the second. Once you choose a comparison; in the third column we'll show how your comparison performed against the baseline, with stats combined over both periods."}
                activeWindow={activeWindow}
                column={currentColumn}
                defaultEnd={defaultEnd}
                defaultMiddle={defaultMiddle}
                defaultStart={defaultStart}
                disposition={"current"}
                setActiveWindow={setActiveWindow}
                setUserDefinedWindow={setUserDefinedWindow}
                userDefinedWindow={userDefinedWindow}
                hideControls={hideControls}
            />}
            {(showComparison && showBaselineVSComparison) && <div className={"kpi-grid-vertical-separation"}/>}
            {showBaselineVSComparison && <KPIPillColumn
                title={titles?.baselineVsComparison || baselineVsComparisonTitle}
                tooltip={"This column compares the Baseline against the Comparison. It shows stats over both periods combined, and shows you trends from one period to the next."}
                activeWindow={activeWindow}
                column={previousToCurrent}
                defaultEnd={defaultEnd}
                defaultMiddle={defaultMiddle}
                defaultStart={defaultStart}
                disposition={"both"}
                setActiveWindow={setActiveWindow}
                setUserDefinedWindow={setUserDefinedWindow}
                userDefinedWindow={userDefinedWindow}
                hideControls={hideControls}
            />}
        </div>

        {showCharts && <hr/>}

        {showCharts && <div className={"chart-grid"}>
            {Object.entries(kpis).map(([key, kpi]) => {
                const className = kpi instanceof CategoricalKPI ? "kpi-chart-categorical" : "kpi-chart";
                return <div key={"chart-container-" + kpi.chartKey()} className={className}>
                    <KPIChart
                        key={key + kpi.chartKey()}
                        kpi={kpi}
                        binSizeDays={binSizeDays}
                    />
                </div>
            })}
        </div>}

    </>
};

